import {
  valueDataToString,
  valueDataVToString,
} from "@smartsuite/automation-conditions";
import type {
  InputData,
  OutputData,
  RecordJobData,
} from "@smartsuite/automation-engine-api";
import { observer } from "mobx-react-lite";
import { translateIfPresent } from "../../../../../../../locales/i18n";
import { useRootStore } from "../../../../../../../stores/hooks";
import {
  OUTPUT_APPLICATION_ID,
  RECORD_ID,
} from "../../../../../../../utils/constants";
import { capitalizeFirstLetter } from "../../../../../../../utils/strings";
import {
  addEmptyValueIfNeeded,
  getInputLabel,
} from "../../../../../ActivityGrid/ActivityGrid.helpers";
import { ActivityDetailsItem } from "../../../ActivityDetailsItem/ActivityDetailsItem";
import { getInputValue } from "../../../GeneralActionResultGroup/utils";
import { useActionDetails } from "../../hooks/useActionDetails";
import type { ActionGroupProps } from "../../types";
import { ActionDetailsContainer } from "../common/ActionDetailsContainer";
import { StyledInputList } from "../common/InputList/styles";
import { WithFindWrapper } from "../common/WithFindWrapper/WithFindWrapper";

const AI_RESPONSE_OUTPUT_ID = "ai-response";
const FULL_LLM_PROMPT_OUTPUT_ID = "full-llm-prompt";
const LLM_PROMPT_OUTPUT_ID = "llm-prompt";
const MODEL_INPUT_ID = "model";
const OUTPUT_TYPE_INPUT_ID = "output_type";
const SUPPRESS_ERROR_INPUT_ID = "suppress_error";

// These inputs are not shown: the prompt is displayed from the "llm-prompt"
// output instead, and the custom output configuration is internal.
const HIDDEN_INPUT_IDS = [
  RECORD_ID,
  OUTPUT_APPLICATION_ID,
  "output_content",
  "propmt",
];

export const AILoopingActionResultGroup = observer<ActionGroupProps>(
  ({
    action,
    recordJobData,
    recordJobDataByDetailsId,
    onDetailsClick,
    onRecordClick,
  }) => {
    const { automations, jobDetails } = useRootStore();
    const { status, errorMessage, actionLabel, selectedActionFallback } =
      useActionDetails(action);

    const getLabel = (input: InputData, index: number): string => {
      // The output type input has no label in the action description
      if (input.inputId === OUTPUT_TYPE_INPUT_ID) {
        const label = "Output Type";
        return translateIfPresent(`caption.${label}`, label);
      }
      const label = getInputLabel(
        input,
        jobDetails,
        action,
        automations.candidate.allActions,
        selectedActionFallback,
        index,
      );
      // The dynamic model input is saved without a label in the action config
      if (label === "" && input.inputId === MODEL_INPUT_ID) {
        const modelLabel = "Model";
        return translateIfPresent(`caption.${modelLabel}`, modelLabel);
      }
      return label;
    };

    const getValue = (input: InputData, recordJob?: RecordJobData): string => {
      if (
        input.inputId === SUPPRESS_ERROR_INPUT_ID ||
        input.inputId === OUTPUT_TYPE_INPUT_ID
      ) {
        const label = capitalizeFirstLetter(
          input.value?.values[0].value.oneofKind === "string"
            ? input.value.values[0].value.string
            : "",
        );
        return translateIfPresent(`caption.${label}`, label);
      }
      return getInputValue(input, jobDetails, recordJob);
    };

    const getOutputValue = (output: OutputData): string => {
      if (output.value !== undefined && output.value.values.length > 0) {
        if (output.value.values[0].value.oneofKind === "list") {
          return output.value.values[0].value.list.values
            .map((value) => valueDataToString(value))
            .join("<br/>");
        }
        return valueDataVToString(output.value.values[0]);
      }
      return "";
    };

    return (
      <ActionDetailsContainer
        label={actionLabel}
        status={status}
        errorMessage={errorMessage}
        onDetailsClick={onDetailsClick}
      >
        <WithFindWrapper
          recordJobData={recordJobData}
          recordJobDataByDetailsId={recordJobDataByDetailsId}
          onRecordClick={onRecordClick}
        >
          {({ recordJobData }) => {
            const outputs = recordJobData?.outputData?.outputs ?? [];
            const aiResponseOutput = outputs.find(
              (output) => output.outputId === AI_RESPONSE_OUTPUT_ID,
            );
            const restOutputs = outputs
              .filter(
                (output) =>
                  ![AI_RESPONSE_OUTPUT_ID, FULL_LLM_PROMPT_OUTPUT_ID].includes(
                    output.outputId,
                  ),
              )
              .sort((a) => (a.outputId === LLM_PROMPT_OUTPUT_ID ? -1 : 0));

            return (
              <StyledInputList>
                {recordJobData?.inputs
                  .filter((input) => !HIDDEN_INPUT_IDS.includes(input.inputId))
                  .map((input, index) => (
                    <li key={input.inputId}>
                      <ActivityDetailsItem label={getLabel(input, index)}>
                        <span
                          dangerouslySetInnerHTML={{
                            __html: addEmptyValueIfNeeded(
                              getValue(input, recordJobData),
                            ),
                          }}
                        />
                      </ActivityDetailsItem>
                    </li>
                  ))}
                {restOutputs.map((output) => (
                  <li key={output.outputId}>
                    <ActivityDetailsItem label={output.outputLabel}>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: addEmptyValueIfNeeded(getOutputValue(output)),
                        }}
                      />
                    </ActivityDetailsItem>
                  </li>
                ))}
                {aiResponseOutput !== undefined && (
                  <li>
                    <ActivityDetailsItem label={aiResponseOutput.outputLabel}>
                      {addEmptyValueIfNeeded(getOutputValue(aiResponseOutput))}
                    </ActivityDetailsItem>
                  </li>
                )}
              </StyledInputList>
            );
          }}
        </WithFindWrapper>
      </ActionDetailsContainer>
    );
  },
);
